/**
 * The authentication surface (API.md §2.2, SECURITY.md §4, ADR-004).
 *
 * ## Why these are typed by hand
 *
 * Better Auth is mounted as a Fastify wildcard at `/api/v1/auth/*`: one route object
 * for a whole path space, which `@nestjs/swagger` cannot enumerate, so none of
 * `sign-in/email`, `email-otp/*`, `sign-up/email` or the reset pair appears in the
 * OpenAPI document. The two routes that *are* ours - `GET /auth/session` and the patient
 * secure link - come from the generated types; the rest are declared below against
 * API.md §2.2 and verified by `apps/api`'s own suite. Docs/PATIENT_AUTH_UI.md lists
 * "publish the auth mount in the OpenAPI document" as an open item for the API.
 *
 * ## What a caller has to know
 *
 * - Every one of these sets or clears the session cookie as a side effect. Nothing is
 *   returned that identifies the session; the next `getSession()` is the source of
 *   truth, which is why the React bindings invalidate `['session']` after each.
 * - `sendEmailCode` and `requestSecureLink` never say whether the address exists
 *   (rules §21.4). The screen that calls them must show the same confirmation either
 *   way, or it becomes the account-existence oracle the API refuses to be.
 * - A locked account answers `429 RATE_LIMITED` with `Retry-After`, identical to a rate
 *   limit, deliberately (SECURITY.md §4.4). Never render the word "locked".
 * - Staff and platform accounts get `{ twoFactorRedirect: true }` from
 *   `signInWithPassword` and **no session** until a second factor is verified. The
 *   patient app has no MFA screen, so it surfaces that as "please use the clinic admin
 *   sign-in"; it must not treat the 200 as a successful sign-in.
 */

import type { RequestOptions } from './client.js';
import { ApiError } from './errors.js';
import type { HttpMethod } from './paths.js';

export interface SignInEmailInput {
  email: string;
  password: string;
}

/**
 * Better Auth's sign-in answer. A patient gets the session shape; an MFA-enrolled
 * account gets the redirect shape and no cookie.
 */
export interface SignInEmailResult {
  redirect?: boolean;
  token?: string | null;
  user?: { id: string; email: string; name?: string | null };
  twoFactorRedirect?: boolean;
  twoFactorMethods?: string[];
}

export interface SignUpEmailInput {
  email: string;
  password: string;
  name: string;
}

export interface SignUpEmailResult {
  token?: string | null;
  user?: { id: string; email: string; name?: string | null };
}

export interface EmailOtpInput {
  email: string;
  otp: string;
}

export interface RequestPasswordResetInput {
  email: string;
  /** Where the emailed link should land. Same-origin path, e.g. `/reset-password`. */
  redirectTo?: string;
}

export interface ResetPasswordInput {
  token: string;
  newPassword: string;
}

export interface SecureLinkInput {
  email: string;
  /** Where to land after the link is consumed. A path inside the app, never a URL. */
  callbackPath?: string;
}

/* ------------------------------------------------------------------ *
 * Staff and platform (M2.8)
 * ------------------------------------------------------------------ */

/** `POST /auth/two-factor/verify-totp`. `trustDevice` skips the second factor for 30 days. */
export interface VerifyTotpInput {
  code: string;
  trustDevice?: boolean;
}

/** What `POST /auth/two-factor/enable` answers for `method: "totp"`. */
export interface TotpEnrolment {
  /** `otpauth://totp/...` - the QR's contents, and the source of the manual setup key. */
  totpURI: string;
  /** Ten single-use codes, in the clear, for the only time they are ever shown. */
  backupCodes: string[];
}

/**
 * WebAuthn options as the passkey plugin serves them: every binary field is base64url,
 * which is why the browser side has to translate before `navigator.credentials`.
 *
 * Typed loosely on purpose. The fields this platform reads are named; the rest travel
 * through untouched, so a plugin upgrade that adds an extension is not a compile error
 * in three SPAs.
 */
export interface PasskeyRegistrationOptions {
  challenge: string;
  rp: { id?: string; name: string };
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: { type: string; alg: number }[];
  timeout?: number;
  excludeCredentials?: { id: string; type?: string; transports?: string[] }[];
  attestation?: string;
  [key: string]: unknown;
}

export interface PasskeyAuthenticationOptions {
  challenge: string;
  timeout?: number;
  rpId?: string;
  allowCredentials?: { id: string; type?: string; transports?: string[] }[];
  userVerification?: string;
  [key: string]: unknown;
}

/** One row of `GET /auth/passkey/list-user-passkeys`. */
export interface PasskeySummary {
  id: string;
  name?: string | null;
  deviceType?: string | null;
  backedUp?: boolean;
  createdAt?: string;
  [key: string]: unknown;
}

/** One row of `GET /auth/list-sessions`. `token` is what revokes it. */
export interface SessionRecord {
  id: string;
  token: string;
  createdAt?: string;
  updatedAt?: string;
  expiresAt?: string;
  ipAddress?: string | null;
  userAgent?: string | null;
  [key: string]: unknown;
}

export interface StartImpersonationInput {
  userId: string;
  tenantId: string;
  /** At least 10 characters. The server refuses below that (API.md §2.13). */
  reason: string;
}

/** `POST /api/v1/platform/impersonation`. */
export interface StartImpersonationResult {
  impersonationSession: {
    id: string;
    tenantId: string;
    tenantSlug: string;
    targetUserId: string;
    targetUserEmail: string;
    adminUserId: string;
    startedAt: string;
    expiresAt: string;
    endedAt: string | null;
  };
  /**
   * Where to send the browser: the tenant's **admin** host, which is a different origin
   * from the super admin console. The caller must treat it as untrusted and refuse
   * anything that is not one of its own known hosts.
   */
  redirectUrl?: string;
}

/** Everything under `client.auth`. */
export interface AuthApi {
  /** Password sign-in. Check `twoFactorRedirect` before treating it as signed in. */
  signInWithPassword(input: SignInEmailInput, options?: RequestOptions): Promise<SignInEmailResult>;
  /** Registers a patient. `population` is forced to `patient` server-side. */
  register(input: SignUpEmailInput, options?: RequestOptions): Promise<SignUpEmailResult>;
  /** Emails a 6-digit code. Always succeeds, whether or not the address exists. */
  sendEmailCode(email: string, options?: RequestOptions): Promise<void>;
  /** Verifies the code and starts the session. */
  signInWithEmailCode(input: EmailOtpInput, options?: RequestOptions): Promise<SignInEmailResult>;
  /** Emails a single-use 15-minute sign-in link. Always `202`. */
  requestSecureLink(input: SecureLinkInput, options?: RequestOptions): Promise<void>;
  /** Consumes a secure link token; `410 LINK_EXPIRED` when it is spent or stale. */
  consumeSecureLink(token: string, options?: RequestOptions): Promise<void>;
  /** Starts a password reset. Always succeeds, whether or not the address exists. */
  requestPasswordReset(input: RequestPasswordResetInput, options?: RequestOptions): Promise<void>;
  /** Finishes a reset with the token from the email. Revokes every other session. */
  resetPassword(input: ResetPasswordInput, options?: RequestOptions): Promise<void>;
  /** Clears the cookie and deletes the session row. */
  signOut(options?: RequestOptions): Promise<void>;

  /* ---------------------------------------------- staff / platform (M2.8) */

  /**
   * Staff and super admin password sign-in.
   *
   * The same endpoint as `signInWithPassword` under a name that says what the caller
   * expects: an enrolled account answers `{ twoFactorRedirect: true }` with **no
   * session**, and the admin surfaces treat that as "now ask for the second factor"
   * rather than as a failure. Two names, one call, because a screen that gets this
   * wrong appears to sign someone in and then finds them anonymous (API.md §2.2).
   */
  signInStaff(input: SignInEmailInput, options?: RequestOptions): Promise<SignInEmailResult>;

  /**
   * Verifies a TOTP code. Two jobs, one route:
   * - after a `twoFactorRedirect` sign-in it issues the session, using the `ha.two_factor`
   *   challenge cookie the sign-in set;
   * - on an existing session it satisfies step-up for the next ten minutes.
   */
  verifyTotp(input: VerifyTotpInput, options?: RequestOptions): Promise<void>;

  /** Recovery at sign-in. Needs the two-factor challenge cookie, not a session. */
  verifyBackupCode(input: { code: string }, options?: RequestOptions): Promise<void>;

  /**
   * Starts TOTP enrolment and returns the secret plus ten backup codes.
   *
   * Enrolment is **not** complete until a code verifies (`skipVerificationOnEnable:
   * false` on the server), so the caller must follow this with `verifyTotp`.
   */
  enableTotp(input: { password: string }, options?: RequestOptions): Promise<TotpEnrolment>;

  /** Replaces every unused backup code. The old set stops working. */
  generateBackupCodes(input: { password: string }, options?: RequestOptions): Promise<string[]>;

  /** Options for registering a passkey. A **GET** on the Better Auth mount. */
  passkeyRegisterOptions(
    input?: { name?: string; authenticatorAttachment?: 'platform' | 'cross-platform' },
    options?: RequestOptions,
  ): Promise<PasskeyRegistrationOptions>;

  /** Finishes registration with the attestation from `navigator.credentials.create`. */
  passkeyRegister(
    input: { response: unknown; name?: string },
    options?: RequestOptions,
  ): Promise<void>;

  /** Options for a passkey assertion. A **GET**, despite what API.md §2.2 says - see §7. */
  passkeyAuthenticateOptions(options?: RequestOptions): Promise<PasskeyAuthenticationOptions>;

  /** Signs in, or satisfies step-up, with the assertion from `navigator.credentials.get`. */
  passkeyAuthenticate(input: { response: unknown }, options?: RequestOptions): Promise<void>;

  /** The caller's own passkeys, for the security page. */
  listPasskeys(options?: RequestOptions): Promise<PasskeySummary[]>;

  /** Removes one passkey. The last remaining factor cannot be removed - the API refuses. */
  deletePasskey(input: { id: string }, options?: RequestOptions): Promise<void>;

  /** The caller's own sessions, newest first as the API returns them. */
  listSessions(options?: RequestOptions): Promise<SessionRecord[]>;

  /** Revokes one session by its token. Revocation is immediate (SECURITY.md §4.4). */
  revokeSession(input: { token: string }, options?: RequestOptions): Promise<void>;

  /**
   * Starts a support session as a tenant user (`platform.impersonate`, **step-up**).
   *
   * The reason is mandatory and at least 10 characters; the API writes the
   * `impersonation_sessions` row and the audit event *before* it issues the session
   * (SECURITY.md §6.3).
   */
  impersonationStart(
    input: StartImpersonationInput,
    options?: RequestOptions,
  ): Promise<StartImpersonationResult>;

  /**
   * Ends the impersonation this session is running under.
   *
   * Deliberately unpermissioned and MFA-exempt on the server: the caller holds the
   * *target's* permissions, and leaving must never be harder than entering (ADR-005).
   *
   * Answers `200 { redirectUrl }`, not `204` (API.md §2.13 note 4): the exit is usually
   * pressed inside the clinic's admin app, on the clinic's host, and the session that
   * took the browser there has just been deleted - a `204` would leave it nowhere to go.
   * The URL is the console's own host, derived from the API's `PLATFORM_DOMAIN` and
   * never from a request header. **Check it before following it anyway**: "the server
   * told us to" is how open redirects get shipped, and a caller that assigns
   * `window.location` from a response body without a same-origin test is one
   * misconfiguration away from being the redirector.
   *
   * `redirectUrl` is `''` when the answer carried none, so a caller has one falsy case
   * to handle rather than two.
   */
  impersonationStop(options?: RequestOptions): Promise<EndImpersonationResult>;
}

/** `DELETE /api/v1/platform/impersonation/current`. */
export interface EndImpersonationResult {
  /** The console's own host, or `''` when the answer carried no body. */
  redirectUrl: string;
}

type RawRequest = <Result>(
  method: HttpMethod,
  path: string,
  body?: unknown,
  options?: RequestOptions,
) => Promise<Result>;

const AUTH = '/api/v1/auth';
const PATIENT_AUTH = '/api/v1/patient/auth';
const PLATFORM = '/api/v1/platform';

export function createAuthApi(request: RawRequest): AuthApi {
  return {
    signInWithPassword: (input, options) =>
      request<SignInEmailResult>('post', `${AUTH}/sign-in/email`, input, options),

    register: (input, options) =>
      request<SignUpEmailResult>('post', `${AUTH}/sign-up/email`, input, options),

    sendEmailCode: async (email, options) => {
      // `type: "sign-in"` is what distinguishes this from address verification; the
      // plugin rejects the call without it.
      await request(
        'post',
        `${AUTH}/email-otp/send-verification-otp`,
        { email, type: 'sign-in' },
        options,
      );
    },

    signInWithEmailCode: (input, options) =>
      request<SignInEmailResult>('post', `${AUTH}/sign-in/email-otp`, input, options),

    requestSecureLink: async (input, options) => {
      await request('post', `${PATIENT_AUTH}/link`, input, options);
    },

    consumeSecureLink: async (token, options) => {
      await request('get', `${PATIENT_AUTH}/link/consume`, undefined, {
        ...options,
        query: { ...options?.query, token },
      });
    },

    requestPasswordReset: async (input, options) => {
      await request('post', `${AUTH}/request-password-reset`, input, options);
    },

    resetPassword: async (input, options) => {
      await request('post', `${AUTH}/reset-password`, input, options);
    },

    signOut: async (options) => {
      await request('post', `${AUTH}/sign-out`, {}, options);
    },

    /* -------------------------------------------- staff / platform (M2.8) */

    signInStaff: (input, options) =>
      request<SignInEmailResult>('post', `${AUTH}/sign-in/email`, input, options),

    verifyTotp: async (input, options) => {
      await request('post', `${AUTH}/two-factor/verify-totp`, input, options);
    },

    verifyBackupCode: async (input, options) => {
      await request('post', `${AUTH}/two-factor/verify-backup-code`, input, options);
    },

    enableTotp: async (input, options) => {
      // `method: "totp"` is the plugin's default, but naming it means an upgrade that
      // changes the default cannot silently enrol an email-OTP factor instead.
      const body = await request<{ totpURI?: string; backupCodes?: string[] }>(
        'post',
        `${AUTH}/two-factor/enable`,
        { password: input.password, method: 'totp' },
        options,
      );
      if (typeof body.totpURI !== 'string' || !Array.isArray(body.backupCodes)) {
        throw new ApiError({
          status: 200,
          code: 'UNEXPECTED_RESPONSE',
          message: 'We could not start two-factor setup. Please try again.',
          correlationId: null,
        });
      }
      return { totpURI: body.totpURI, backupCodes: body.backupCodes };
    },

    generateBackupCodes: async (input, options) => {
      const body = await request<{ backupCodes?: string[] }>(
        'post',
        `${AUTH}/two-factor/generate-backup-codes`,
        input,
        options,
      );
      if (!Array.isArray(body.backupCodes)) {
        throw new ApiError({
          status: 200,
          code: 'UNEXPECTED_RESPONSE',
          message: 'We could not generate new backup codes. Please try again.',
          correlationId: null,
        });
      }
      return body.backupCodes;
    },

    passkeyRegisterOptions: (input, options) =>
      request<PasskeyRegistrationOptions>(
        'get',
        `${AUTH}/passkey/generate-register-options`,
        undefined,
        {
          ...options,
          query: {
            ...options?.query,
            ...(input?.name === undefined ? {} : { name: input.name }),
            ...(input?.authenticatorAttachment === undefined
              ? {}
              : { authenticatorAttachment: input.authenticatorAttachment }),
          },
        },
      ),

    passkeyRegister: async (input, options) => {
      await request('post', `${AUTH}/passkey/verify-registration`, input, options);
    },

    passkeyAuthenticateOptions: (options) =>
      request<PasskeyAuthenticationOptions>(
        'get',
        `${AUTH}/passkey/generate-authenticate-options`,
        undefined,
        options,
      ),

    passkeyAuthenticate: async (input, options) => {
      await request('post', `${AUTH}/passkey/verify-authentication`, input, options);
    },

    listPasskeys: async (options) => {
      const body = await request<PasskeySummary[] | { passkeys?: PasskeySummary[] }>(
        'get',
        `${AUTH}/passkey/list-user-passkeys`,
        undefined,
        options,
      );
      // The plugin returns a bare array; tolerate a wrapper so a shape change is an
      // empty list rather than a crash on the security page.
      if (Array.isArray(body)) return body;
      return Array.isArray(body.passkeys) ? body.passkeys : [];
    },

    deletePasskey: async (input, options) => {
      await request('post', `${AUTH}/passkey/delete-passkey`, input, options);
    },

    listSessions: async (options) => {
      const body = await request<SessionRecord[] | { sessions?: SessionRecord[] }>(
        'get',
        `${AUTH}/list-sessions`,
        undefined,
        options,
      );
      if (Array.isArray(body)) return body;
      return Array.isArray(body.sessions) ? body.sessions : [];
    },

    revokeSession: async (input, options) => {
      await request('post', `${AUTH}/revoke-session`, input, options);
    },

    impersonationStart: async (input, options) => {
      const body = await request<{ data: StartImpersonationResult }>(
        'post',
        `${PLATFORM}/impersonation`,
        input,
        options,
      );
      return body.data;
    },

    impersonationStop: async (options) => {
      const body = await request<{ redirectUrl?: string } | null>(
        'delete',
        `${PLATFORM}/impersonation/current`,
        undefined,
        options,
      );
      return { redirectUrl: body?.redirectUrl ?? '' };
    },
  };
}
